import { type FC } from 'react';
import { useDispatch } from 'react-redux';
import {
  addressRelatedColumnRoles, ColumnRole,
} from '~/_shared/types/columnRole.enum';
import type { SpreadsheetMatchupData } from '~/_shared/types/spreadsheetData/matchupData';
import { BoundaryGroupCodenames } from '~/boundary/boundary.types';
import { useGeneralBoundaryGroups } from '~/store/boundaryGroups/boundaryGroups.selectors';
import { BoundaryTerritoryType } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroup.type';
import {
  boundaryTerritoryGroupActivate,
  boundaryTerritoryGroupsCreateRequest,
} from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.actionCreators';
import {
  BoundaryMatchingType, type BoundaryTerritoryGroupMatchingsRequest,
} from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.repository';
import type { BoundaryTerritoryGroup } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.state';
import { useBoundaryToolActiveBTGsSelector } from '~/store/boundaryTerritoryGroups/boundaryTool/boundaryToolBTGs.selectors';
import {
  useBoundaryFormPreselectedBoundaryGroupCodename, useBoundaryFormPreselectedBoundaryTerritoryType, useBoundaryFormPreselectedDemographicName,
} from '~/store/frontendState/mapTools/boundary/boundaryForm/boundaryForm.selectors';
import { boundaryTerritoryGroupHighlightStarted } from '~/store/frontendState/mapTools/boundary/boundarySidebarState/boundarySidebarState.actionCreators';
import {
  mapSettingsBoundariesSetPrimaryBoundaryGroupId, mapSettingsBoundaryGroupOrderGroupHoisted,
} from '~/store/mapSettings/boundaries/mapSettingsBoundaries.actionCreators';
import { useSpreadsheetMatchupDataSelector } from '~/store/matchupData/matchupData.selectors';
import { useClientIdSelector } from '~/store/selectors/useClientIdSelector';
import { useMapIdSelector } from '~/store/selectors/useMapIdSelector';
import { usePrimarySpreadsheetId } from '~/store/selectors/usePrimarySpreadsheetId';
import { createBoundaryTerritoryGroupRequestParams } from '../../boundaryTerritoryGroup.helpers';
import { useVerifyNumericFillData } from '../../fill/useVerifyNumericFillData';
import {
  type LoadGlobalBoundaryGroupData, LoadGlobalBoundaryGroupPaneComponent,
} from './loadGlobalBoundaryGroupPane.component';

type LoadGlobalBoundaryGroupPaneContainerProps = Readonly<{
  checkIsNumeric: (columnId: string | null, spreadsheetId: number | null) => Promise<boolean>;
  resetForm: () => void;
}>;

const onlyHasZipCategoryMapped = (matchupData: SpreadsheetMatchupData | null) => {
  if (!matchupData) {
    return false;
  }

  const isCategoryMapped = (categoryMapping: SpreadsheetMatchupData['categories'][ColumnRole]) =>
    categoryMapping.match !== null;

  let isZipMapped = false;
  let isOtherGeolocationMapped = false;

  Object.entries(matchupData.categories).forEach(([categoryName, categoryMapping]) => {
    if ((categoryName === ColumnRole.PostCode) && isCategoryMapped(categoryMapping)) {
      isZipMapped = true;
    }
    else if ((addressRelatedColumnRoles as readonly string[]).includes(categoryName) && isCategoryMapped(categoryMapping)) {
      isOtherGeolocationMapped = true;
    }
  });

  return isZipMapped && !isOtherGeolocationMapped;
};

export const LoadGlobalBoundaryGroupPaneContainer: FC<LoadGlobalBoundaryGroupPaneContainerProps> = props => {
  const globalBoundaryGroups = useGeneralBoundaryGroups();
  const clientId = useClientIdSelector();
  const preselectedCodename = useBoundaryFormPreselectedBoundaryGroupCodename();
  const initialBoundaryGroupId = preselectedCodename === null
    ? null
    : (globalBoundaryGroups.find(g => g.codename === preselectedCodename)?.id ?? null);
  const initialBoundaryTerritoryType = useBoundaryFormPreselectedBoundaryTerritoryType();
  const preselectedDemographicName = useBoundaryFormPreselectedDemographicName();
  const preselectedGroup = globalBoundaryGroups.find(g => g.id === initialBoundaryGroupId) ?? null;
  const initialDemographicId = preselectedDemographicName === null
    ? null
    : (preselectedGroup?.demographics.find(d => d.name === preselectedDemographicName)?.id ?? null);
  const { verifyNumericFillData } = useVerifyNumericFillData({ checkIsNumeric: props.checkIsNumeric });
  const activeBoundaryTerritoryGroups = useBoundaryToolActiveBTGsSelector();
  const mapId = useMapIdSelector();
  const dispatch = useDispatch();
  const spreadsheetId = usePrimarySpreadsheetId();
  const matchupData = useSpreadsheetMatchupDataSelector(spreadsheetId);
  const preferZipMatching = onlyHasZipCategoryMapped(matchupData);

  const handleAddBoundary = (data: LoadGlobalBoundaryGroupData) => {
    if ((mapId === null) || (clientId === null)) {
      return;
    }

    const onSuccess = (boundaryTerritoryGroup: BoundaryTerritoryGroup) => {
      // Set the newly created BTG as active
      dispatch(boundaryTerritoryGroupActivate(boundaryTerritoryGroup));

      dispatch(mapSettingsBoundaryGroupOrderGroupHoisted(
        boundaryTerritoryGroup.boundaryGroupId,
      ));

      dispatch(mapSettingsBoundariesSetPrimaryBoundaryGroupId(boundaryTerritoryGroup.boundaryGroupId));

      dispatch(boundaryTerritoryGroupHighlightStarted(boundaryTerritoryGroup.boundaryGroupId));
    };

    const createAndDispatch = () => {
      const activeBoundaryTerritoryGroup = activeBoundaryTerritoryGroups
        .find(btg => btg.boundaryGroupId === data.boundaryGroupId);
      const requestParams = createBoundaryTerritoryGroupRequestParams({
        mapId,
        boundaryGroupId: data.boundaryGroupId,
        territoryType: data.boundaryTerritoryType,
        fillData: data.boundaryFillData,
        demographicId: data.demographicId,
        numericCalculateBucketFunction: data.numericCalculateBucketFunction,
      }, activeBoundaryTerritoryGroup);

      const boundaryGroup = globalBoundaryGroups.find(b => b.id === data.boundaryGroupId);

      const zipMatchings: BoundaryTerritoryGroupMatchingsRequest | undefined = (
        preferZipMatching &&
        matchupData?.categories?.[ColumnRole.PostCode]?.match &&
        boundaryGroup?.codename === BoundaryGroupCodenames.UsZipCodes
      ) ? {
          matching_type: BoundaryMatchingType.Zips,
          match_columns: [{ category: ColumnRole.PostCode, column_id: matchupData.categories[ColumnRole.PostCode].match }],
        } : undefined;

      dispatch(boundaryTerritoryGroupsCreateRequest({
        ...requestParams,
        ...(zipMatchings ? { matchings: zipMatchings } : {}),
      }, onSuccess));
      props.resetForm();
    };

    // Handle numeric territory type with validation
    if (data.boundaryTerritoryType === BoundaryTerritoryType.Numeric) {
      verifyNumericFillData(data.boundaryFillData)
        .then(isNumeric => {
          if (isNumeric && data.boundaryFillData && data.boundaryGroupId !== null) {
            createAndDispatch();
          }
        });
    }
    else {
      // Handle all other territory types
      createAndDispatch();
    }
  };

  return (
    <LoadGlobalBoundaryGroupPaneComponent
      globalBoundaryGroups={globalBoundaryGroups}
      initialBoundaryGroupId={initialBoundaryGroupId}
      initialBoundaryTerritoryType={initialBoundaryTerritoryType}
      initialDemographicId={initialDemographicId}
      onAddBoundary={handleAddBoundary}
    />
  );
};
